/**
 * Input validation utilities for security and data integrity
 */

import { logger } from './logger';

// Maximum lengths for different input types
export const INPUT_LIMITS = {
  SEARCH_QUERY: 100,
  GENERAL_TEXT: 255,
  EMAIL: 254,
  NAME: 50
} as const;

// Allowed character patterns
export const PATTERNS = {
  // Allow letters, numbers, spaces, and common punctuation for search
  SEARCH_SAFE: /^[a-zA-Z0-9\s\-_.,àáâãäåæçèéêëìíîïðñòóôõöøùúûüýþÿ]*$/,
  // Alphanumeric with spaces
  ALPHANUMERIC_SPACES: /^[a-zA-Z0-9\s]*$/,
  // Numbers only
  NUMERIC: /^[0-9]*$/,
  // Email pattern (basic)
  EMAIL: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
  // NGAP code pattern (letters and numbers with dots/commas)
  NGAP_CODE: /^[A-Z]{2,4}\s*[0-9]+[.,]?[0-9]*$/i
} as const;

// Dangerous patterns to block
export const DANGEROUS_PATTERNS = [
  // Script tags
  /<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi,
  // SQL injection patterns
  /(\b(SELECT|INSERT|UPDATE|DELETE|DROP|CREATE|ALTER|EXEC|UNION)\b)/gi,
  // JavaScript execution patterns
  /(javascript:|data:text\/html|vbscript:|onload=|onerror=)/gi,
  // Command injection patterns
  /[;&|`$(){}[\]\\]/g,
  // HTML tags (basic detection)
  /<[^>]*>/g
] as const;

export interface ValidationResult {
  isValid: boolean;
  sanitizedValue: string;
  errors: string[];
}

/**
 * Validates and sanitizes search input
 */
export function validateSearchInput(input: string): ValidationResult {
  logger.debug('validateSearchInput: Initial input:', input);
  const errors: string[] = [];
  const sanitizedValue = input;

  // Check if input is empty
  if (!input || typeof input !== 'string') {
    logger.debug('validateSearchInput: Input is empty or not string, returning empty.');
    return {
      isValid: true,
      sanitizedValue: '',
      errors: []
    };
  }

  // Check length
  if (input.length > INPUT_LIMITS.SEARCH_QUERY) {
    errors.push(`La recherche ne peut pas dépasser ${INPUT_LIMITS.SEARCH_QUERY} caractères`);
  }

  // Check for dangerous patterns
  for (const pattern of DANGEROUS_PATTERNS) {
    if (pattern.test(sanitizedValue)) {
      errors.push('Caractères non autorisés détectés');
      break; // Stop checking other patterns once we find one
    }
  }

  // Validate against safe pattern
  if (sanitizedValue && !PATTERNS.SEARCH_SAFE.test(sanitizedValue)) {
    errors.push('Seuls les lettres, chiffres, espaces et tirets sont autorisés');
  }

  return {
    isValid: errors.length === 0,
    sanitizedValue,
    errors
  };
}

/**
 * Validates NGAP code format
 */
export function validateNgapCode(code: string): ValidationResult {
  const errors: string[] = [];
  let sanitizedValue = code.trim().toUpperCase();

  if (!code) {
    return { isValid: true, sanitizedValue: '', errors: [] };
  }

  // Check length
  if (sanitizedValue.length > 20) {
    errors.push('Le code NGAP ne peut pas dépasser 20 caractères');
    sanitizedValue = sanitizedValue.substring(0, 20);
  }

  // Check format
  if (!PATTERNS.NGAP_CODE.test(sanitizedValue)) {
    errors.push('Format de code NGAP invalide (ex: AMK 7.3)');
  }

  return {
    isValid: errors.length === 0,
    sanitizedValue,
    errors
  };
}

/**
 * Escapes HTML characters to prevent XSS
 */
export function escapeHtml(text: string): string {
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
}

/**
 * Validates numeric input
 */
export function validateNumericInput(value: string, min?: number, max?: number): ValidationResult {
  const errors: string[] = [];
  let sanitizedValue = value.trim();

  if (!value) {
    return { isValid: true, sanitizedValue: '', errors: [] };
  }

  // Check if numeric
  if (!PATTERNS.NUMERIC.test(sanitizedValue)) {
    errors.push('Seuls les chiffres sont autorisés');
    sanitizedValue = sanitizedValue.replace(/[^0-9]/g, '');
  }

  // Check range
  const numValue = parseInt(sanitizedValue, 10);
  if (!isNaN(numValue)) {
    if (min !== undefined && numValue < min) {
      errors.push(`La valeur doit être supérieure ou égale à ${min}`);
    }
    if (max !== undefined && numValue > max) {
      errors.push(`La valeur doit être inférieure ou égale à ${max}`);
    }
  }

  return {
    isValid: errors.length === 0,
    sanitizedValue,
    errors
  };
}